// src/components/sourcex/ExportSourceDocModal.jsx
//
// Export an assembled source document for printing / Save-as-PDF.
//
// Presentation only — it reads the template, builds a printable HTML page for the
// chosen visit, and opens it in a new tab. It writes nothing clinical; the only
// thing it persists is the header/footer config (PI, version, …) back onto the
// template so a coordinator does not re-type it every time.

import { useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { X, Printer, FileDown, Loader2, ShieldCheck, FileWarning } from "lucide-react";
import { useDialog } from "./useDialog";
import { describeActor } from "../../services/approverIdentity";
import { eventIsApproved, eventIsPaperFinal } from "../../services/templateSchema";
import { db } from "../../firebase";
import { getStudiesPath, getSourcexTemplatesPath } from "../../services/firestorePaths";
import {
  buildSourceDocHtml,
  defaultExportConfig,
  exportStudyIdFor,
  footerDateFor,
} from "../../services/sourceDocExport";
import { countVisitApprovals } from "../../services/templateVersionService";
import {
  downloadSourceDocx,
  sourceDocxFilename,
} from "../../services/sourceDocDocx";

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 transition";

const todayStr = () => {
  const d = new Date();
  const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    d.getMonth()
  ];
  return `${String(d.getDate()).padStart(2, "0")}-${mon}-${d.getFullYear()}`;
};

export default function ExportSourceDocModal({
  studyId,
  template,
  defaultEventId,
  onClose,
  // When true the export renders recorded answers (patient export). Defaults to
  // false so the template page's blank-form export is completely unaffected.
  withAnswers = false,
  // Patient export only: prefill the header's Patient Number / Initials.
  patientNumber = "",
  patientInitials = "",
}) {
  const events = useMemo(
    () => (template?.sections || []).flatMap((s) => s.events || []),
    [template]
  );
  // Default to the visit the user is already viewing, so exporting doesn't
  // silently reset to the first (Screening) visit. Falls back to the first
  // event when opened somewhere without a specific visit.
  const [eventId, setEventId] = useState(
    (defaultEventId && events.some((e) => e.id === defaultEventId)
      ? defaultEventId
      : events[0]?.id) || ""
  );
  const [config, setConfig] = useState(null);
  const { alert: showAlert, confirm: askConfirm, dialog } = useDialog();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Prefill from the real study doc (the assembled template records its
  // sourceStudyId) plus any config saved on the template from a prior export.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Never the template id as-is: a built template is opened at
      // `{studyId}__assembled`, and reading a study doc under that id found
      // nothing and left the whole header blank. See exportStudyIdFor.
      const realStudyId = exportStudyIdFor(template, studyId);
      let study = {};
      try {
        const snap = await getDoc(doc(db, getStudiesPath(), realStudyId));
        if (snap.exists()) study = snap.data();
      } catch (_) {
        /* defaults are fine if the study doc can't be read */
      }
      if (cancelled) return;
      setConfig({
        ...defaultExportConfig(study, template?.exportConfig || {}, todayStr()),
        // The footer date is WHEN THE VISIT WAS APPROVED, and it is derived at
        // the moment of export from the visit itself (see `exportConfig`
        // below) rather than held in state — one config serves a picker that
        // can switch visits, and each visit has its own approval date. Cleared
        // here so a date saved by an older export can never print.
        date: "",
        // Autofilled for the patient export; editable below before exporting.
        ...(patientNumber ? { patientNumber } : {}),
        ...(patientInitials ? { patientInitials } : {}),
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId, template, patientNumber, patientInitials]);

  // Prefill Version from how many times THIS VISIT has been approved: a visit
  // on its third approval exports as "Version 3.0". Counted per visit because
  // approval is per visit — Screening being on revision 3 says nothing about
  // Visit 3. Re-runs when the visit picker changes.
  //
  // A PREFILL, not a lock: the field stays editable, and a version the user has
  // already typed is never overwritten. A visit never approved keeps the usual
  // "Version 1.0".
  const versionTouched = useRef(false);
  useEffect(() => {
    if (!eventId || versionTouched.current) return;
    let cancelled = false;
    (async () => {
      const n = await countVisitApprovals(studyId, eventId);
      if (cancelled || !n) return;
      setConfig((c) => (c ? { ...c, version: `Version ${n}.0` } : c));
    })();
    return () => {
      cancelled = true;
    };
  }, [studyId, eventId]);

  // The visit being exported, and the config as it will actually print. The
  // footer date belongs to the VISIT, not to the export run: an approved
  // source document is identified by "Version 2.0 · 09-Aug-2026", and a date
  // that moved every time somebody re-printed the same approved form made
  // that pair worthless. Derived here so switching the visit picker can never
  // leave one visit's approval date on another's document.
  const selected = useMemo(
    () => events.find((e) => e.id === eventId) || events[0] || null,
    [events, eventId]
  );
  const exportConfig = useMemo(
    () => (config ? { ...config, date: footerDateFor(selected) } : null),
    [config, selected]
  );

  const set = (k, v) => {
    // Once the version has been typed in, the approval-count prefill above
    // stops overwriting it — changing the visit picker must not silently undo
    // what someone deliberately entered.
    if (k === "version") versionTouched.current = true;
    setConfig((c) => ({ ...c, [k]: v }));
  };

  const persistConfig = async () => {
    // Only the template page saves its header config back to the template doc.
    // A patient export must not write to the template — it just reads config.
    if (withAnswers) return;
    try {
      // Three fields are deliberately NOT saved:
      //   date  — the visit's approval date, derived on every export (see
      //           exportConfig above). Storing it would leave one visit's
      //           approval date saved on a template shared by all of them.
      //   patientNumber / patientInitials — a template is a blank form shared
      //           by every patient in the study and has no per-patient audit
      //           trail, so a patient identifier must never be written onto
      //           it. They apply to the one export being run and nothing more.
      const {
        date: _exportDate,
        patientNumber: _pn,
        patientInitials: _pi,
        ...saved
      } = config;
      await updateDoc(doc(db, getSourcexTemplatesPath(), studyId), { exportConfig: saved });
    } catch (_) {
      // Persisting the header config is a convenience, never a blocker on the
      // export itself.
    }
  };

  // Who signed this document off, if anyone. Approval is per event, so this
  // covers a visit, a standing log and the eligibility checklist alike — all
  // three are events, and all three are exported through this modal.
  const approvedBy = eventIsApproved(selected)
    ? describeActor(selected?.approvedBy)
    : "";

  // Marked in the template editor as having been finalized ON PAPER — this
  // study's real copy of this document is a signed printout, usually because
  // the visit was done before the site moved into SourceX. Independent of
  // approval: a paper-final document may never have been approved here at all,
  // and an approved one may also have a paper original. Both can be true, and
  // when they are, both are said.
  const paperFinalBy = eventIsPaperFinal(selected)
    ? describeActor(selected?.paperFinalBy)
    : "";

  // WHY A COORDINATOR IS ASKED BEFORE EXPORTING AN APPROVED DOCUMENT
  //
  // RegX does not reproduce every formatting change a filed source document
  // ends up carrying — coordinators adjust spacing, page breaks and the like in
  // Word after exporting. That is fine while one person does it, and a problem
  // the moment a second person exports the SAME approved version, applies
  // different adjustments, and two documents claiming to be "Version 2.0" look
  // different. A monitor reads that as an uncontrolled document.
  //
  // So the export asks first, and names the approver: they are the person who
  // knows what was changed after the last export. It does not block anything —
  // an approved document must always be exportable — it just makes sure nobody
  // downloads one without knowing to ask.
  //
  // Asked once per visit per time this modal is open. Every click would train
  // people to dismiss it without reading, which is how a warning stops working;
  // switching the visit picker asks again, because that is a different
  // document with a different approver.
  //
  // The paper-final marker rides in the same confirm rather than a second one:
  // both say "someone else holds the authoritative copy of this, go ask them",
  // and two dialogs in a row for one click is how people learn to click through
  // without reading. When both are true they are both stated — they can be
  // different people, and the paper one is the more serious of the two.
  const acknowledged = useRef(new Set());
  const confirmApprovedExport = async () => {
    const name = selected?.eventName || "This document";
    if ((!approvedBy && !paperFinalBy) || acknowledged.current.has(eventId)) {
      return true;
    }
    const lines = [];
    if (paperFinalBy) {
      lines.push(
        `${paperFinalBy} has marked “${name}” as FINALIZED ON PAPER. The signed copy for this study is that printout — please check with them for it before exporting, and don't file what comes out of here as a second source.`
      );
    }
    if (approvedBy) {
      const when = exportConfig?.date ? ` on ${exportConfig.date}` : "";
      lines.push(
        `“${name}” was approved by ${approvedBy}${when}. RegX can't reproduce every formatting change made to an exported document, so check with them whether this version carries any before you file or share what you export. Two copies of the same approved version that don't match is a monitoring finding.`
      );
    }
    const ok = await askConfirm(lines.join("\n\n"), {
      title: paperFinalBy
        ? "This document was finalized on paper"
        : "This document is approved",
      confirmLabel: "I'll check — export",
      // The paper case is the one where exporting can actively create a second
      // source document, so it is the one drawn as a warning.
      ...(paperFinalBy ? { tone: "danger" } : {}),
    });
    if (ok) acknowledged.current.add(eventId);
    return ok;
  };

  const openPrintable = async () => {
    const event = events.find((e) => e.id === eventId) || events[0];
    if (!event) return;
    if (!(await confirmApprovedExport())) return;
    await persistConfig();
    const html = buildSourceDocHtml({ event, config: exportConfig, withAnswers });
    const w = window.open("", "_blank");
    if (!w) {
      await showAlert(
        "Please allow pop-ups for this site to open the printable document.",
        { title: "Pop-up blocked", tone: "danger" }
      );
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const downloadDocx = async () => {
    const event = events.find((e) => e.id === eventId) || events[0];
    if (!event || downloading) return;
    if (!(await confirmApprovedExport())) return;
    setDownloading(true);
    try {
      await persistConfig();
      await downloadSourceDocx({
        event,
        config: exportConfig,
        filename: sourceDocxFilename(event, exportConfig),
        withAnswers,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to build .docx", err);
      await showAlert("Sorry — the Word document could not be generated.", {
        title: "Download failed",
        tone: "danger",
      });
    } finally {
      setDownloading(false);
    }
  };

  const field = (label, key, placeholder = "") => (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
      <input
        className={inputCls}
        value={config?.[key] || ""}
        placeholder={placeholder}
        onChange={(e) => set(key, e.target.value)}
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Centered above this modal, not pinned to the top of the window. */}
      {dialog}
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0c0726] p-5 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-100 inline-flex items-center gap-2">
            <FileDown size={18} /> Export source document
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200">
            <X size={18} />
          </button>
        </div>

        {loading || !config ? (
          <div className="flex items-center gap-2 py-8 text-slate-400">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <label className="block mb-4">
              <span className="text-[11px] uppercase tracking-wide text-slate-400">Visit</span>
              <select
                className={inputCls}
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              >
                {events.map((e) => (
                  <option key={e.id} value={e.id} className="bg-[#0c0726]">
                    {e.eventName}
                  </option>
                ))}
              </select>
            </label>

            {/* The same fact the confirm carries, standing in the modal — so
                it is read while choosing the visit rather than only in a box
                that appears once the export is already in motion. */}
            {/* Paper first, and drawn louder: this is the one where exporting
                can create a SECOND source document for a visit that already
                has a signed one. */}
            {paperFinalBy && (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/[0.12] px-3 py-2">
                <FileWarning size={15} className="mt-0.5 shrink-0 text-rose-300" />
                <p className="text-[11px] leading-relaxed text-rose-100/90">
                  <strong>{paperFinalBy}</strong> marked this as finalized on
                  paper. The signed copy for this study is that printout — check
                  with them for it, and don't file what you export here as a
                  second source.
                </p>
              </div>
            )}

            {approvedBy && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-400/[0.08] px-3 py-2">
                <ShieldCheck size={15} className="mt-0.5 shrink-0 text-amber-300" />
                <p className="text-[11px] leading-relaxed text-amber-100/90">
                  Approved by <strong>{approvedBy}</strong>
                  {exportConfig?.date ? ` on ${exportConfig.date}` : ""}. Check with
                  them whether this version carries formatting changes made after
                  it was exported — the same approved version must not be filed
                  looking two different ways.
                </p>
              </div>
            )}

            <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">
              Header — appears at the top of every page
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field("Sponsor", "sponsor", "BI")}
              {field("Study #", "studyId", "1397-0014")}
              {field("Study name", "studyName", "AIRTIVITY")}
              {field("Site", "site", "USA100")}
              {field("Principal Investigator", "pi", "Sandeep Bansal, MD")}
              {field("Patient # prefix", "patientPrefix", "USA100-")}
              {/* Offered on the TEMPLATE export too, not just the patient one.
                  Without it, the prefix box was the only place to put a patient
                  number — so anyone exporting for a named patient typed the
                  whole number into it, and the header still printed the
                  write-in line after it ("USA100-0123______"). Left blank these
                  behave exactly as before: a blank form with a line to write
                  on. Neither is saved back to the template (see persistConfig)
                  — a patient number must not end up on a document shared by
                  every patient in the study. */}
              {field("Patient # (after prefix)", "patientNumber", "leave blank for a write-in line")}
              {field("Patient initials", "patientInitials", "leave blank for a write-in line")}
            </div>

            <div className="mt-4 mb-2 text-[11px] uppercase tracking-wide text-slate-500">
              Footer — appears at the bottom of every page
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field("Version", "version", "Version 1.0")}
              {/* Locked: the footer date is the date this visit was APPROVED,
                  which is a fact about the document, not a field to set. Shown
                  rather than hidden so it is clear what will print. */}
              <label className="block">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">
                  Date
                </span>
                <input
                  className={`${inputCls} opacity-70 cursor-not-allowed`}
                  value={exportConfig?.date || ""}
                  readOnly
                  disabled
                  title="The date this visit was approved — not editable"
                />
                <span className="mt-1 block text-[10px] text-slate-500">
                  {selected?.approvedAt
                    ? "The date this visit was approved. It changes only when the visit is re-approved."
                    : "This visit has never been approved, so it prints as a draft dated today."}
                </span>
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={openPrintable}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
              >
                <Printer size={15} /> Print / Save as PDF
              </button>
              <button
                onClick={downloadDocx}
                disabled={downloading}
                className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-400/20 disabled:opacity-60"
              >
                {downloading ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <FileDown size={15} />
                )}
                {downloading ? "Building…" : "Download .docx"}
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              <strong className="text-slate-400">Download .docx</strong> saves an editable Word
              document in the filed source format (repeating header, boxed sections, tables, and a
              “Page X of Y” footer). <strong className="text-slate-400">Print / Save as PDF</strong>{" "}
              opens a clean paginated preview in a new tab for the browser's Print dialog.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
